import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const cardStyle = {
  background: 'white',
  borderRadius: '14px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: '600',
  color: '#444'
};

const tableHead = {
  padding: '14px',
  textAlign: 'left',
  background: '#1a237e',
  color: 'white',
  fontSize: '13px'
};

const tableCell = {
  padding: '14px',
  borderBottom: '1px solid #eee',
  fontSize: '14px'
};

const totalRowCell = {
  padding: '14px',
  borderTop: '2px solid #1a237e',
  fontSize: '14px',
  fontWeight: '700',
  background: '#f0f2fb'
};

const MODES = [
  { value: 'all', label: 'All Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'schedule', label: 'Schedule-wise' },
  { value: 'custom', label: 'Custom Range' }
];

const ReportsGeneration = () => {

  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState([]); // ✅ flat: one row per invigilator per exam
  const [grandTotal, setGrandTotal] = useState(0);
  const [schedules, setSchedules] = useState([]);

  const [totals, setTotals] = useState({
    total_invigilators: 0,
    total_remuneration: 0
  });

  const [mode, setMode] = useState('all');

  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    week_start: '',
    month: '',
    schedule_id: ''
  });

  // ==========================================
  // LOAD SCHEDULES (for schedule-wise dropdown)
  // ==========================================
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const res = await axios.get('/schedules');
        setSchedules(res.data || []);
      } catch (err) {
        console.log(err);
      }
    };
    fetchSchedules();
  }, []);

  // ==========================================
  // FETCH REPORT
  // ==========================================
  const fetchReport = async () => {

    try {

      setLoading(true);

      const params = { mode };

      if (mode === 'custom') {
        if (!filters.from_date || !filters.to_date) {
          toast.error('Please pick both From Date and To Date');
          setLoading(false);
          return;
        }
        params.from_date = filters.from_date;
        params.to_date = filters.to_date;
      }

      if (mode === 'weekly') {
        if (!filters.week_start) {
          toast.error('Please pick a week start date');
          setLoading(false);
          return;
        }
        params.week_start = filters.week_start;
      }

      if (mode === 'monthly') {
        if (!filters.month) {
          toast.error('Please pick a month');
          setLoading(false);
          return;
        }
        params.month = filters.month;
      }

      if (mode === 'schedule') {
        if (!filters.schedule_id) {
          toast.error('Please pick a schedule/exam');
          setLoading(false);
          return;
        }
        params.schedule_id = filters.schedule_id;
      }

      const res = await axios.get(
        '/reports/remuneration-summary',
        { params }
      );

      console.log('REPORT RESPONSE:', res.data);

      if (res.data?.success) {

        setSummary(res.data.reports || []);
        setGrandTotal(Number(res.data.grand_total || 0));

        setTotals({
          total_invigilators: res.data.total_invigilators || 0,
          total_remuneration: res.data.grand_total || 0
        });

      } else {

        setSummary([]);
        setGrandTotal(0);

        setTotals({
          total_invigilators: 0,
          total_remuneration: 0
        });
      }

    } catch (err) {

      console.log(err);

      toast.error(
        err.response?.data?.message ||
        'Failed to load report data'
      );

      setSummary([]);
      setGrandTotal(0);

    } finally {

      setLoading(false);
    }
  };

  // ==========================================
  // INITIAL LOAD — defaults to "All Time" so nothing is silently hidden
  // ==========================================
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================
  // HANDLE INPUT
  // ==========================================
  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleModeChange = (e) => {
    setMode(e.target.value);
  };

  // ==========================================
  // SEARCH / RESET
  // ==========================================
  const handleSearch = () => {

    if (
      mode === 'custom' &&
      filters.from_date &&
      filters.to_date &&
      filters.from_date > filters.to_date
    ) {
      toast.error('From Date cannot be greater than To Date');
      return;
    }

    fetchReport();
  };

  const handleReset = () => {
    setMode('all');
    setFilters({
      from_date: '',
      to_date: '',
      week_start: '',
      month: '',
      schedule_id: ''
    });
    setTimeout(() => {
      fetchReport();
    }, 100);
  };

  // ==========================================
  // EXPORT PDF — flat layout: Exam Type column shows the exam name
  // ==========================================
  const exportPDF = () => {

    if (summary.length === 0) {
      toast.error('No data to export');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to download the PDF');
      return;
    }

    const rowsHtml = summary.map((item, index) => `
      <tr>
        <td>${index + 1}.</td>
        <td style="text-align:left;">${item.invigilator_name}</td>
        <td>${item.department || '-'}</td>
        <td>${item.exam_name}</td>
        <td>${item.shifts_worked}</td>
        <td><strong>${Number(item.total_amount || 0).toFixed(0)}</strong></td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Remuneration Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 30px; color: #000; }
            h2 { text-align: center; text-decoration: underline; margin-bottom: 24px; font-size: 18px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #000; padding: 8px 10px; text-align: center; font-size: 13px; }
            th { background: #f0f0f0; font-weight: 700; }
            tfoot td { font-weight: 700; background: #f0f0f0; }
            .signature { margin-top: 70px; text-align: right; font-weight: 600; font-size: 13px; }
            .sign-line { display: inline-block; width: 220px; border-top: 1px solid #000; margin-bottom: 4px; }
            @media print {
              body { padding: 10px; }
            }
          </style>
        </head>
        <body>
          <h2>Details of Online Exams & Remuneration of Manpower</h2>
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Beneficiary Name</th>
                <th>Department</th>
                <th>Exam Type</th>
                <th>Shifts Worked</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5">Grand Total</td>
                <td>${Number(grandTotal || 0).toFixed(0)}</td>
              </tr>
            </tfoot>
          </table>
          <div class="signature">
            <div class="sign-line"></div><br/>
            Controller of Examinations
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  // ==========================================
  // UI
  // ==========================================
  return (

    <div style={{ padding: '32px', background: '#f5f6fa', minHeight: '100vh' }}>

      {/* HEADER */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#1a237e', marginBottom: '8px' }}>Reports Generation</h2>
        <p style={{ color: '#666' }}>Details of Online Exams & Remuneration of Manpower</p>
      </div>

      {/* FILTERS */}
      <div style={{ ...cardStyle, marginBottom: '24px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '18px' }}>
          <div>
            <label style={labelStyle}>Report Type</label>
            <select value={mode} onChange={handleModeChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Conditional inputs per mode */}
          <div style={{ display: 'flex', gap: '14px', alignItems: 'end' }}>

            {mode === 'custom' && (
              <>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>From Date</label>
                  <input type="date" name="from_date" value={filters.from_date} onChange={handleChange} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>To Date</label>
                  <input type="date" name="to_date" value={filters.to_date} onChange={handleChange} style={inputStyle} />
                </div>
              </>
            )}

            {mode === 'weekly' && (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Week Starting</label>
                <input type="date" name="week_start" value={filters.week_start} onChange={handleChange} style={inputStyle} />
              </div>
            )}

            {mode === 'monthly' && (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Month</label>
                <input type="month" name="month" value={filters.month} onChange={handleChange} style={inputStyle} />
              </div>
            )}

            {mode === 'schedule' && (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Exam / Schedule</label>
                <select name="schedule_id" value={filters.schedule_id} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                  <option value="">-- Select Exam --</option>
                  {schedules.map(s => (
                    <option key={s.id} value={s.id}>{s.exam_name}</option>
                  ))}
                </select>
              </div>
            )}

            {mode === 'all' && (
              <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                Showing every recorded payout across all exams.
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSearch} style={{
            padding: '11px 18px', background: '#1a237e', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
          }}>
            Search
          </button>

          <button onClick={handleReset} style={{
            padding: '11px 18px', background: '#757575', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
          }}>
            Reset
          </button>

          <button onClick={exportPDF} style={{
            padding: '11px 18px', background: '#b71c1c', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
          }}>
            Download PDF
          </button>
        </div>

      </div>

      {/* SUMMARY CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>

        <div style={cardStyle}>
          <h3 style={{ margin: 0, color: '#1a237e' }}>Total Beneficiaries</h3>
          <p style={{ fontSize: '34px', fontWeight: '700', margin: '12px 0 0', color: '#333' }}>
            {totals.total_invigilators}
          </p>
        </div>

        <div style={cardStyle}>
          <h3 style={{ margin: 0, color: '#1a237e' }}>Total Amount</h3>
          <p style={{ fontSize: '34px', fontWeight: '700', margin: '12px 0 0', color: '#2e7d32' }}>
            ₹ {Number(totals.total_remuneration || 0).toFixed(2)}
          </p>
        </div>

      </div>

      {/* REPORT TABLE — flat: one row per invigilator per exam, Exam Type column shows exam name */}
      <div style={cardStyle}>

        <h3 style={{ color: '#1a237e', marginBottom: '20px' }}>
          Details of Online Exams & Remuneration of Manpower
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading report...</div>
        ) : summary.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#777' }}>No report data found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHead}>S.No</th>
                  <th style={tableHead}>Beneficiary Name</th>
                  <th style={tableHead}>Department</th>
                  <th style={tableHead}>Exam Type</th>
                  <th style={tableHead}>Shifts Worked</th>
                  <th style={tableHead}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((item, index) => (
                  <tr key={`${item.invigilator_id}-${item.schedule_id}`}>
                    <td style={tableCell}>{index + 1}.</td>
                    <td style={{ ...tableCell, fontWeight: '600' }}>{item.invigilator_name}</td>
                    <td style={tableCell}>{item.department || '-'}</td>
                    <td style={tableCell}>{item.exam_name}</td>
                    <td style={tableCell}>{item.shifts_worked}</td>
                    <td style={{ ...tableCell, fontWeight: '700', color: '#2e7d32' }}>
                      {Number(item.total_amount || 0).toFixed(0)}
                    </td>
                  </tr>
                ))}

                <tr>
                  <td style={totalRowCell} colSpan={5}>Grand Total</td>
                  <td style={{ ...totalRowCell, color: '#2e7d32' }}>
                    {Number(grandTotal || 0).toFixed(0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

      </div>

    </div>
  );
};

export default ReportsGeneration;